/**
 * Ronde 4 W7 — Notificatie & Signalering Engine (spec ronde-4/07 §16).
 *
 * Gedeelde enums, labels, DTO's en Zod-schemas voor de NotificationRule-engine.
 * Server (apps/api) en frontend (backoffice meldingen-instellingen) delen deze
 * definities. Payloads zijn `Record<string, unknown>` — nooit `any` (CLAUDE.md).
 */

import { z } from 'zod'
import { queryBoolean } from './zod-query'
import { AANVULLENDE_RESOLVER_LABELS } from './notificatie-resolver-labels'
import {
  NOTIFICATIE_RECIPIENT_RESOLVER_KEYS,
  NOTIFICATIE_EVENT_TYPE_KEY,
  type NotificatieRecipientResolverKey,
} from './notificaties'

// ── Enums ────────────────────────────────────────────────────────────────────

export const NOTIFICATION_RULE_CATEGORY = [
  'PLANNING',
  'COMPLIANCE',
  'HR',
  'OPERATIONEEL',
  'SYSTEEM',
  'KWALITEIT',
] as const
export type NotificationRuleCategory = (typeof NOTIFICATION_RULE_CATEGORY)[number]

export const NOTIFICATION_RULE_CATEGORY_LABELS: Record<NotificationRuleCategory, string> = {
  PLANNING: 'Planning',
  COMPLIANCE: 'Compliance / Arbo',
  HR: 'HR / Verzuim',
  OPERATIONEEL: 'Operationeel',
  SYSTEEM: 'Systeem',
  KWALITEIT: 'Kwaliteit',
}

export const NOTIFICATION_TRIGGER_TYPE = [
  'REALTIME',
  'SCHEDULE',
  'THRESHOLD',
  'AGGREGATE',
] as const
export type NotificationTriggerType = (typeof NOTIFICATION_TRIGGER_TYPE)[number]

export const NOTIFICATION_TRIGGER_TYPE_LABELS: Record<NotificationTriggerType, string> = {
  REALTIME: 'Realtime (bij een actie)',
  SCHEDULE: 'Periodiek (cron)',
  THRESHOLD: 'Bij drempel-overschrijding',
  AGGREGATE: 'Verzameld over periode',
}

export const NOTIFICATION_PRIORITY = [
  'LOW',
  'MEDIUM',
  'HIGH',
  'CRITICAL',
  'BLOCKING',
] as const
export type NotificationPriority = (typeof NOTIFICATION_PRIORITY)[number]

export const NOTIFICATION_PRIORITY_LABELS: Record<NotificationPriority, string> = {
  LOW: 'Laag',
  MEDIUM: 'Normaal',
  HIGH: 'Hoog',
  CRITICAL: 'Kritiek',
  BLOCKING: 'Blokkerend',
}

export const NOTIFICATION_EVENT_STATUS = [
  'VERSTUURD',
  'GELEZEN',
  'IN_BEHANDELING',
  'OPGELOST',
  'GENEGEERD',
  'GEESCALEERD',
  'VERLOPEN',
] as const
export type NotificationEventStatus = (typeof NOTIFICATION_EVENT_STATUS)[number]

export const NOTIFICATION_EVENT_STATUS_LABELS: Record<NotificationEventStatus, string> = {
  VERSTUURD: 'Verstuurd',
  GELEZEN: 'Gelezen',
  IN_BEHANDELING: 'In behandeling',
  OPGELOST: 'Opgelost',
  GENEGEERD: 'Genegeerd',
  GEESCALEERD: 'Geëscaleerd',
  VERLOPEN: 'Verlopen',
}

export const NOTIFICATION_COOLDOWN_TYPE = ['ONCE', 'DAILY', 'CUSTOM', 'ALWAYS'] as const
export type NotificationCooldownType = (typeof NOTIFICATION_COOLDOWN_TYPE)[number]

export const NOTIFICATION_COOLDOWN_TYPE_LABELS: Record<NotificationCooldownType, string> = {
  ONCE: 'Eenmalig per entiteit',
  DAILY: 'Max 1x per dag',
  CUSTOM: 'Aangepast interval',
  ALWAYS: 'Elke keer',
}

/** Kanalen — spiegel van NotificatieKanaalType (string-array voor configuratie). */
export const NOTIFICATION_CHANNEL = ['IN_APP', 'PUSH', 'EMAIL'] as const
export type NotificationChannel = (typeof NOTIFICATION_CHANNEL)[number]

// ── DTOs ─────────────────────────────────────────────────────────────────────

export interface NotificationRuleDTO {
  id: string
  code: string
  naam: string
  beschrijving: string | null
  category: NotificationRuleCategory
  triggerType: NotificationTriggerType
  priority: NotificationPriority
  mandatory: boolean
  mutable: boolean
  enabled: boolean
  defaultChannels: string[]
  cooldownType: NotificationCooldownType
  cooldownMinutes: number
  recipientResolver: string
  escalationEnabled: boolean
  escalationAfterHours: number | null
  escalationTarget: string | null
  escalationMaxLevels: number
  eventTypeKey: string | null
  /** Per-regel drempel-configuratie (bijv. { inactiefDagen: 14 }). */
  triggerConfig?: Record<string, number | string | boolean>
  /** Aantal events ooit gevuurd voor deze regel. */
  eventCount?: number
  /** Aantal events dat nog open staat (status VERSTUURD/IN_BEHANDELING). */
  openCount?: number
  /**
   * Aantal keer dat deze regel recent (laatste `recentEventWindowDays` dagen)
   * is afgegaan — afgaat-frequentie voor het beoordelen van het nut (FO-014 §5).
   */
  recentEventCount?: number
  /** Venster (dagen) waarover `recentEventCount` is geteld. */
  recentEventWindowDays?: number
}

/** Standaard-venster (dagen) voor de afgaat-frequentie in het beheer-scherm. */
export const NOTIFICATION_RULE_RECENT_WINDOW_DAYS = 30

export interface NotificationRuleEventDTO {
  id: string
  ruleCode: string
  ruleNaam: string
  entityType: string
  entityId: string
  severity: NotificationPriority
  status: NotificationEventStatus
  triggeredAt: string
  resolvedAt: string | null
  resolvedBy: string | null
  resolution: string | null
  recipientIds: string[]
  payload: Record<string, unknown>
}

export interface NotificationEscalationDTO {
  id: string
  ruleId: string
  eventId: string
  level: number
  escalatedToRole: string | null
  escalatedToUserId: string | null
  reason: string
  escalatedAt: string
  resolvedAt: string | null
  resolvedBy: string | null
}

export interface NotificationRulePreferenceDTO {
  ruleId: string
  ruleCode: string
  ruleNaam: string
  category: NotificationRuleCategory
  priority: NotificationPriority
  mandatory: boolean
  mutable: boolean
  /** Effectieve kanaal-instellingen (rule-default tenzij user-override). */
  inApp: boolean
  push: boolean
  email: boolean
  muted: boolean
  snoozedUntil: string | null
}

export interface NotificationRuleStatsDTO {
  total: number
  resolved: number
  escalated: number
  open: number
  /** Gemiddelde reactietijd in minuten (null als geen opgeloste events). */
  averageResponseMinutes: number | null
}

export interface NotificationDashboardDTO {
  perCategorie: Array<{ category: NotificationRuleCategory; count: number; open: number }>
  recentEvents: NotificationRuleEventDTO[]
  escalaties: number
  gemiddeldeResponseTijd: number | null // minuten
}

// ── Zod schemas ────────────────────────────────────────────────────────────────

export const updateNotificationRuleSchema = z
  .object({
    enabled: z.boolean().optional(),
    priority: z.enum(NOTIFICATION_PRIORITY).optional(),
    defaultChannels: z.array(z.enum(NOTIFICATION_CHANNEL)).optional(),
    cooldownType: z.enum(NOTIFICATION_COOLDOWN_TYPE).optional(),
    cooldownMinutes: z.number().int().nonnegative().optional(),
    escalationEnabled: z.boolean().optional(),
    escalationAfterHours: z.number().int().positive().nullable().optional(),
    escalationTarget: z.string().min(1).nullable().optional(),
    escalationMaxLevels: z.number().int().positive().max(5).optional(),
    mutable: z.boolean().optional(),
    triggerConfig: z.record(z.string(), z.union([z.number(), z.string(), z.boolean()])).optional(),
  })
  .strict()
export type UpdateNotificationRuleInput = z.infer<typeof updateNotificationRuleSchema>

export const updateNotificationEventStatusSchema = z
  .object({
    status: z.enum(['IN_BEHANDELING', 'OPGELOST', 'GENEGEERD'] as const),
    resolution: z.string().max(2000).optional(),
  })
  .strict()
export type UpdateNotificationEventStatusInput = z.infer<
  typeof updateNotificationEventStatusSchema
>

export const muteRuleSchema = z
  .object({
    /** Minuten; weglaten = permanente mute. */
    snoozeDuration: z.number().int().positive().optional(),
  })
  .strict()
export type MuteRuleInput = z.infer<typeof muteRuleSchema>

export const updateRulePreferenceSchema = z
  .object({
    inApp: z.boolean().optional(),
    push: z.boolean().optional(),
    email: z.boolean().optional(),
  })
  .strict()
export type UpdateRulePreferenceInput = z.infer<typeof updateRulePreferenceSchema>

export const listNotificationRulesQuerySchema = z
  .object({
    category: z.enum(NOTIFICATION_RULE_CATEGORY).optional(),
    triggerType: z.enum(NOTIFICATION_TRIGGER_TYPE).optional(),
    enabled: queryBoolean().optional(),
  })
  .strict()

export const listNotificationEventsQuerySchema = z
  .object({
    ruleId: z.string().optional(),
    status: z.enum(NOTIFICATION_EVENT_STATUS).optional(),
    entityType: z.string().optional(),
    entityId: z.string().optional(),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(200).default(50),
  })
  .strict()

// ── Nieuwe signalering aanmaken (FO-014 §5 — regel-bouwstenen) ─────────────────
//
// Gedeeld schema: de backoffice-aanmaak-dialog valideert er client-side mee én
// de API parset er de body mee. Eén bron → het form kan niets sturen dat de API
// weigert. `mandatory` zit BEWUST niet in het schema: verplichte/compliance-
// regels worden via de seed beheerd, niet via deze admin-aanmaak (een
// admin-eigen regel is altijd uit te zetten/te muten).
export const createNotificationRuleSchema = z
  .object({
    /** Optioneel — server genereert een unieke code uit de naam als leeg. */
    code: z
      .string()
      .trim()
      .regex(
        /^[A-Z][A-Z0-9_]*$/,
        'Code mag alleen hoofdletters, cijfers en _ bevatten en met een letter beginnen',
      )
      .max(80)
      .optional(),
    naam: z.string().trim().min(1, 'Naam is verplicht').max(160),
    beschrijving: z.string().trim().max(2000).nullable().optional(),
    category: z.enum(NOTIFICATION_RULE_CATEGORY),
    triggerType: z.enum(NOTIFICATION_TRIGGER_TYPE),
    priority: z.enum(NOTIFICATION_PRIORITY).default('MEDIUM'),
    defaultChannels: z.array(z.enum(NOTIFICATION_CHANNEL)).min(1, 'Kies minstens één kanaal'),
    cooldownType: z.enum(NOTIFICATION_COOLDOWN_TYPE).default('DAILY'),
    cooldownMinutes: z.number().int().nonnegative().max(100000).default(0),
    recipientResolver: z.enum(NOTIFICATIE_RECIPIENT_RESOLVER_KEYS),
    eventTypeKey: z.enum(NOTIFICATIE_EVENT_TYPE_KEY).nullable().optional(),
    titleTemplate: z.string().trim().max(200).nullable().optional(),
    bodyTemplate: z.string().trim().max(1000).nullable().optional(),
    linkTemplate: z.string().trim().max(300).nullable().optional(),
    triggerConfig: z
      .record(z.string(), z.union([z.number(), z.string(), z.boolean()]))
      .optional(),
    escalationEnabled: z.boolean().default(false),
    escalationAfterHours: z.number().int().positive().max(720).nullable().optional(),
    escalationTarget: z.string().trim().min(1).max(80).nullable().optional(),
    escalationMaxLevels: z.number().int().positive().max(5).default(1),
  })
  .strict()
  .superRefine((val, ctx) => {
    // Zonder gekoppeld event-type heeft de regel een eigen body-template nodig,
    // anders krijgt de melding geen inhoud (engine valt terug op naam/leeg).
    if (!val.eventTypeKey && (!val.bodyTemplate || val.bodyTemplate.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['bodyTemplate'],
        message: 'Bericht-tekst is verplicht als er geen gekoppeld event-type is',
      })
    }
    // Escalatie aan → termijn verplicht (anders escaleert de cron nooit).
    if (
      val.escalationEnabled &&
      (val.escalationAfterHours == null || val.escalationAfterHours <= 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['escalationAfterHours'],
        message: 'Geef een escalatietermijn (uren) op als escalatie aanstaat',
      })
    }
  })
export type CreateNotificationRuleInput = z.infer<typeof createNotificationRuleSchema>

// ── Doelgroep-resolvers — mens-leesbaar (FO-014 §5 "wie krijgt het") ───────────
//
// Subset van alle resolver-keys die een ADMIN/management logisch bij een eigen
// signalering kan kiezen; de overige keys zijn intern gebonden aan specifieke
// bron-entiteiten (taak/gesprek/onboarding/voorraad/etc.) en horen niet in de
// vrije aanmaak-dropdown. `resolverLabel()` geeft voor elke key een leesbare
// omschrijving (fallback = de kale key) voor de "wie krijgt het"-weergave.
export const SELECTABLE_RECIPIENT_RESOLVERS: ReadonlyArray<{
  key: NotificatieRecipientResolverKey
  label: string
}> = [
  { key: 'all-hr', label: 'HR' },
  { key: 'all-hr+salaris', label: 'HR + salarisadministratie' },
  { key: 'leave.approvers', label: 'Teamleiders + HR (beoordelaars)' },
  { key: 'leave.teamleiders', label: 'Teamleider van de medewerker' },
  { key: 'planner', label: 'Planners' },
  { key: 'planner+teamleider', label: 'Planners + teamleiders' },
  { key: 'planner+hr', label: 'Planners + HR' },
  { key: 'manager', label: 'Management' },
  { key: 'hr+manager', label: 'HR + management' },
  { key: 'employee.self', label: 'De betrokken medewerker' },
  { key: 'employee.self+tl', label: 'Medewerker + teamleider' },
  { key: 'employee.self+hr', label: 'Medewerker + HR' },
  { key: 'employee.self+hr+tl', label: 'Medewerker + HR + teamleider' },
  { key: 'administratie', label: 'Administratie (facturen)' },
  { key: 'client.responsible', label: 'Verantwoordelijke van de klant' },
  { key: 'lead.owner', label: 'Eigenaar van de lead' },
]

const RESOLVER_LABEL_LOOKUP: Record<string, string> = Object.fromEntries(
  SELECTABLE_RECIPIENT_RESOLVERS.map((r) => [r.key, r.label]),
)

/**
 * batch-56 notificatie-override (FO-416) - labels voor de resolvers die je NIET
 * vrij bij een eigen signalering kiest, maar die wél in het ontvangers-scherm
 * langskomen (elk event heeft er een). Bewust een APARTE map: die keys horen
 * niet in de aanmaak-dropdown hierboven, maar een scherm vol kale keys als
 * `hr-gesprek.betrokkenen` is onleesbaar.
 *
 * Onvolledig is niet erg: `resolverLabel` valt terug op de kale key, en die is
 * nog altijd sprekender dan een verzonnen omschrijving.
 */
const RESOLVER_WEERGAVE_LABELS: Record<string, string> = {
  'actor.requester': 'De aanvrager zelf',
  'employee.self': 'De betrokken medewerker',
  'werkbon.assigned-employee': 'De monteur op de werkbon',
  'werkbon.previous-employee': 'De vorige monteur op de werkbon',
  'werkbon.teamleider': 'Teamleider van de monteur',
  'intake.team': 'Intake-team',
  // batch-60 klantportaal-zichtbaar-verzoeken (FO-439b K4)
  'klantverzoeken.ontvangers': 'Ontvangersgroep Klantverzoeken',
  'klantverzoeken.escalatie': 'Escalatie klantverzoeken (managers)',
  'voorraad.beheer': 'Voorraadbeheer',
  'voertuigen.beheer': 'Wagenparkbeheer',
  'voertuigen.beheer+kostprijs': 'Wagenparkbeheer met kostprijsinzage',
  'skills.beoordelaars': 'Beoordelaars skillmatrix',
  // batch-70 voertuig-meldingen (FO-527)
  'voertuig-melding.doelgroep': 'Wagenpark of salarisadministratie (per kanaal)',
  // batch-62 telefoonabonnementen (FO-463)
  'abonnementen.beheer': 'Abonnementenbeheer',
  'hr-gesprek.betrokkenen': 'Betrokkenen bij het gesprek',
  'kandidaat.recruiter-of-hr': 'Recruiter (of HR)',
  'kandidaat.beoordelaars': 'Second-review-beoordelaars (HR, teamleider, directie)',
  'kandidaat.besluitnemers': 'Besluitnemers sollicitatie',
  'kandidaat.info-verzoek': 'Degene van wie informatie gevraagd wordt',
  'verjaardag.medewerker': 'De jarige medewerker',
  'uitrusting.besteller': 'De besteller',
  'all-admins': 'Beheerders (ADMIN)',
  'verzuim.contactpersonen': 'Verzuim-contactpersonen',
  'veiligheid.opvolgers': 'Veiligheidskundigen',
  'jubileum.hr+teamleider': 'HR + teamleider',
  'planning.planners': 'Planners',
  'taak.uitvoerder': 'De uitvoerder van de taak',
  'taak.uitvoerder-tl': 'Uitvoerder + teamleider',
  'taak.teamleider': 'Teamleider van de uitvoerder',
  'ongevallen.beheerders': 'Ongevallenbeheer',
  'onboarding.hr+medewerker': 'HR + de nieuwe medewerker',
  'loonstrook.verantwoordelijken': 'Loonstrook-verantwoordelijken',
  'schade.bestuurder': 'De bestuurder',
  'facturen.approvers': 'Facturen: wie mag goedkeuren',
  'inkoop.behandelaar': 'De inkoop-behandelaar',
  'inkoopbudget.budgethouder': 'De budgethouder',
  'reisafwijking.beoordelaars': 'Beoordelaars reiscorrecties',
  'mail.afzender-of-admin': 'De afzender (of beheer)',
  'idee.indiener': 'De indiener van het idee',
  'idee.ontvangers': 'Kwaliteit-postvak',
  'idee.organisatie': 'Alle medewerkers',
  'gesprek.recipient': 'De betrokkene van het gesprek',
  // batch-65 voorraad-besteladvies (FO-481)
  'inkoop.goedkeurders': 'Inkoop: wie mag goedkeuren',
  'voorraad.minimumbeheer': 'Voorraadbeheer (minimumniveaus wijzigen)',
  // batch-69 magazijn-cijfers (FO-520)
  'voorraad.goedkeurders': 'Voorraad: wie tellingcorrecties mag goedkeuren',
  // FO-508 (spec 208)
  'middel-uitzondering.beoordelaars': 'HR en directie (losse koppeling telefoon/simkaart)',
  // batch-66 contract-mijlpalen (FO-496)
  directie: 'Directie',
  facilities: 'Facilities (uitrusting en wagenpark)',
  'contract-mijlpaal.doelgroep': 'Doelgroep van de mijlpaal (uit het sjabloon)',
  'contract-mijlpaal.escalatie': 'Escalatie-doelgroep van de mijlpaal (uit het sjabloon)',
  'contract-mijlpaal.hr': 'HR, zonder de medewerker zelf (terugval: beheerders)',
  // batch-66 uren-loonstroken (FO-492)
  'loonstrook.vrijgave-bewakers':
    'Loonstrook-verantwoordelijken (anders wie loonstroken mag bewerken)',
  // batch-66 inkoop-afwijkingen (FO-497)
  'boetes.rijgedrag': 'HR en de teamleider van de medewerker (alleen persoonlijk)',
  // batch-67 taken-engine (FO-498)
  'taak.deadline-escalatie': 'De aanmaker van de taak (anders de escalatie-ontvanger van de takenmodule)',
}

/** Mens-leesbaar label voor een doelgroep-resolver (fallback = de kale key). */
export function resolverLabel(key: string): string {
  return (
    RESOLVER_LABEL_LOOKUP[key] ??
    RESOLVER_WEERGAVE_LABELS[key] ??
    // batch-67 (FO-500): aanvullende labels, in een eigen bestand (geen merge-conflict hier).
    AANVULLENDE_RESOLVER_LABELS[key] ??
    key
  )
}

// ── Escalatie-doelgroepen (FO-108) ─────────────────────────────────────────────
//
// `escalationTarget` is een vrij string-veld op `NotificationRule`.
//
// batch-67 (FO-500 §B3): het veld ROUTEERT niet meer. Wie een escalatie krijgt is
// een instelling op de sleutel `<ruleCode>:escalatie` bij Ontvangers per melding;
// zonder instelling de ontvangers van de melding zelf. De migratie heeft het
// bestaande doel van de niet-financiele regels daarheen overgezet (zie
// packages/db/seed/escalatie-doel.ts). Het veld en deze labels blijven alleen
// bestaan als beschrijving op bestaande regels en op het escalatie-record
// (`NotificationEscalation.escalatedToRole`); het beheerscherm biedt de keuze
// niet meer aan.
export const ESCALATION_TARGETS = [
  { key: 'TEAMLEIDER', label: 'Teamleider' },
  { key: 'HR', label: 'HR' },
  { key: 'PLANNER', label: 'Planner' },
  { key: 'SALARIS', label: 'Salarisadministratie' },
  { key: 'MANAGEMENT', label: 'Management' },
  { key: 'ADMIN', label: 'Beheer (ADMIN)' },
] as const
export type EscalationTarget = (typeof ESCALATION_TARGETS)[number]['key']

const ESCALATION_TARGET_LABEL_LOOKUP: Record<string, string> = Object.fromEntries(
  ESCALATION_TARGETS.map((t) => [t.key, t.label]),
)

/** Mens-leesbaar label voor een escalatie-doelgroep (fallback = de kale key). */
export function escalationTargetLabel(key: string): string {
  return ESCALATION_TARGET_LABEL_LOOKUP[key] ?? key
}
